import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Cloud, CloudUpload, HardDrive, Loader2, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";

const gb = (bytes) => `${(bytes / 1024 ** 3).toFixed(2)} GB`;

const Bar = ({ pct }) => (
  <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
    <div
      className={`h-full transition-[width] duration-500 ${pct > 85 ? "bg-red-500" : "bg-cyan-500"}`}
      style={{ width: `${Math.min(pct, 100)}%` }}
    />
  </div>
);

const Stat = ({ label, value, testid }) => (
  <div>
    <p className="mono text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
    <p data-testid={testid} className="font-display text-lg text-slate-900">
      {value}
    </p>
  </div>
);

export const StorageUsage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(null);
  const poll = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/storage");
      setData(res.data);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setLoading(false);
    }
  };

  const watchSync = () => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const { data: state } = await api.get("/admin/storage/sync");
      setSync(state);
      if (!state.running) {
        clearInterval(poll.current);
        toast.success(
          `Sync finished — ${state.uploaded} uploaded, ${state.skipped} already durable` +
            (state.failed ? `, ${state.failed} failed` : "")
        );
        load();
      }
    }, 2000);
  };

  const startSync = async () => {
    try {
      const { data: state } = await api.post("/admin/storage/sync", { prefix: "" });
      setSync(state);
      toast.success("Uploading local files to object storage…");
      watchSync();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  useEffect(() => {
    load();
    api
      .get("/admin/storage/sync")
      .then(({ data: state }) => {
        setSync(state);
        if (state.running) watchSync();
      })
      .catch(() => undefined);
    return () => clearInterval(poll.current);
  }, []);

  if (!data) return <p className="text-sm text-slate-500">Loading storage usage…</p>;

  const object = data.object_storage?.enabled ? data.object_storage : null;
  const notConfigured = data.object_storage && !data.object_storage.enabled
    ? data.object_storage
    : null;
  const quota = object?.quota_bytes || 0;
  const objectPct = quota ? Math.round((object.total_bytes / quota) * 100) : 0;
  const cachePct = Math.round((data.disk_used_bytes / data.disk_total_bytes) * 100);

  return (
    <div className="space-y-5" data-testid="storage-usage">
      {notConfigured ? (
        <div
          data-testid="object-storage-disabled"
          className="rounded-2xl border border-amber-300 bg-amber-50 p-5"
        >
          <h2 className="mono flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-amber-700">
            <Cloud size={13} /> Object storage not active
          </h2>
          <p className="mt-2 text-[12px] text-amber-800">
            Files are stored on this server's disk only (STORAGE_PROVIDER=
            {notConfigured.provider || "unset"}
            {notConfigured.has_key ? "" : ", EMERGENT_LLM_KEY missing"}). They will be lost when the
            container is redeployed or restarted. Add STORAGE_PROVIDER=emergent,
            STORAGE_REMOTE_PREFIX and EMERGENT_LLM_KEY to this environment, redeploy, then run the
            sync from this page.
          </p>
        </div>
      ) : null}
      {object ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="object-storage-card">
          <div className="flex items-center justify-between">
            <h2 className="mono flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-600">
              <Cloud size={13} /> Object storage (durable)
            </h2>
            <div className="flex items-center gap-2">
              <button
                data-testid="storage-sync-button"
                onClick={startSync}
                disabled={sync?.running}
                className="mono flex items-center gap-1.5 rounded-full bg-cyan-600 px-3 py-1 text-[10px] uppercase tracking-wider text-white hover:bg-cyan-500 disabled:opacity-60"
              >
                {sync?.running ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <CloudUpload size={11} />
                )}
                Sync to object storage
              </button>
              <button
                data-testid="storage-refresh-button"
                onClick={load}
                className="mono flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
              >
                <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>
          </div>

          {sync && (sync.running || sync.finished_at) ? (
            <p
              data-testid="storage-sync-status"
              className="mono mt-3 text-[10px] uppercase tracking-wider text-cyan-700"
            >
              {sync.running
                ? `Syncing… ${sync.uploaded + sync.skipped}/${sync.total || "?"} files · ${sync.uploaded} uploaded`
                : `Last sync: ${sync.uploaded} uploaded · ${sync.skipped} already durable${
                    sync.failed ? ` · ${sync.failed} failed` : ""
                  }`}
            </p>
          ) : null}

          {quota ? <Bar pct={objectPct} /> : <div className="mt-4" />}

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Stored" value={gb(object.total_bytes)} testid="object-total" />
            <Stat label="Models (.3dm)" value={gb(object.sources_bytes)} testid="object-sources" />
            <Stat label="Viewer assets" value={gb(object.assets_bytes)} testid="object-assets" />
            <Stat
              label={quota ? "Quota" : "Quota"}
              value={quota ? gb(quota) : "not set"}
              testid="object-quota"
            />
          </div>

          <p className="mono mt-4 text-[10px] uppercase tracking-wider text-slate-500">
            {object.objects} objects · prefix {object.prefix}
            {quota ? ` · ${gb(object.free_bytes)} free (${objectPct}% used)` : ""}
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            Counted from the revisions that still exist. Deleted revisions can leave objects
            behind — the object storage API has no delete operation.
          </p>
          {!quota ? (
            <p data-testid="object-quota-hint" className="mt-2 text-xs text-slate-500">
              Set OBJECT_STORAGE_QUOTA_GB in the backend environment to show your Emergent object
              storage capacity here.
            </p>
          ) : null}
          {quota && objectPct > 85 ? (
            <p data-testid="object-quota-warning" className="mt-2 text-xs text-red-600">
              Object storage is over 85% of your quota — delete unused revisions.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="mono flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-600">
            <HardDrive size={13} /> {object ? "Local cache disk" : "Disk usage"}
          </h2>
          {object ? null : (
            <button
              data-testid="storage-refresh-button"
              onClick={load}
              className="mono flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
            >
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          )}
        </div>

        <Bar pct={cachePct} />

        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Used" value={gb(data.disk_used_bytes)} testid="storage-used" />
          <Stat label="Free" value={gb(data.disk_free_bytes)} testid="storage-free" />
          <Stat label="Total" value={gb(data.disk_total_bytes)} testid="storage-total" />
          <Stat label="Models" value={gb(data.app_storage_bytes)} testid="storage-models" />
        </div>

        <p className="mono mt-4 text-[10px] uppercase tracking-wider text-slate-500">
          Upload limit:{" "}
          {data.max_model_upload_mb > 0 ? `${data.max_model_upload_mb} MB` : "unlimited"} · root{" "}
          {data.storage_root}
        </p>
        {cachePct > 85 ? (
          <p data-testid="storage-warning" className="mt-2 text-xs text-red-600">
            {object
              ? "Cache disk is over 85% full — it refills itself from object storage after a restart."
              : "Disk is over 85% full — archive old revisions before uploading large models."}
          </p>
        ) : null}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="mono text-[11px] uppercase tracking-[0.2em] text-slate-600">Per project</h2>
        <div className="mt-3 divide-y divide-slate-100">
          {data.projects.length === 0 ? (
            <p className="py-3 text-sm text-slate-500">No model assets stored yet.</p>
          ) : (
            data.projects.map((project) => (
              <div
                key={project.project_id}
                data-testid={`storage-project-${project.project_id}`}
                className="flex items-center justify-between py-2.5 text-sm"
              >
                <span className="text-slate-800">
                  {project.code ? `${project.code} · ` : ""}
                  {project.name}
                </span>
                <span className="mono text-xs text-slate-600">{gb(project.bytes)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
